import {
  INVOICE_MAIL_RECIPIENT_PATTERN,
  type InvoiceMailBlockedCode,
  type InvoiceMailLogEntry,
  type InvoiceMailSender,
  type InvoiceMailTemplateOption,
  type InvoicePdfFormat,
} from '@embertime/shared'

// #536: pure decisions of the invoice send sheet, kept apart from React so the
// node test environment can check them without a DOM.

export const BLOCKED_SENDER_TEXT: Record<InvoiceMailBlockedCode, string> = {
  INVOICE_MAIL_EXTERNAL_SMTP: 'externer SMTP ohne Journalweg',
  INVOICE_MAIL_NO_COMPANY: 'keiner Firma zugeordnet',
  INVOICE_MAIL_NO_JOURNAL: 'kein Journal-Archiv',
  INVOICE_MAIL_ACCOUNT_DISABLED: 'Konto deaktiviert',
}

const SEND_ERROR_TEXT: Record<string, string> = {
  INVOICE_MAIL_STALE:
    'Rechnung, Vorlage oder Absender wurden inzwischen geändert. Bitte die neue Vorschau prüfen.',
  INVOICE_MAIL_RESEND_REQUIRED:
    'Diese Rechnung wurde bereits versendet. Bitte erneutes Senden bewusst bestätigen.',
  INVOICE_MAIL_SMTP_FAILED: 'Der Mailserver hat die Rechnung nicht angenommen.',
  INVOICE_MAIL_SMTP_UNKNOWN:
    'Versandausgang unklar. Bitte das Versandprotokoll prüfen und nicht blind erneut senden.',
  INVOICE_MAIL_IN_PROGRESS: 'Die Rechnung wird bereits gesendet.',
  INVOICE_MAIL_WARMUP: 'Rechnungsversand ist bis zum Abschluss der Zustellprüfung gesperrt.',
  INVOICE_MAIL_NO_JOURNAL: 'Für diese Firma ist kein Journal-Archiv eingerichtet.',
  INVOICE_MAIL_EXTERNAL_SMTP: 'Externer SMTP-Absender: Rechnungsversand gesperrt.',
  INVOICE_MAIL_NO_COMPANY: 'Absender ist keiner Firma zugeordnet.',
  INVOICE_MAIL_RECIPIENT_INVALID: 'Bitte eine gültige E-Mail-Adresse eingeben.',
  SMTP_TARGET_BLOCKED: 'Der Postausgang dieses Kontos ist nicht erlaubt.',
}

export function sendErrorText(code: string | undefined, fallback: string): string {
  return (code && SEND_ERROR_TEXT[code]) || fallback
}

export function recipientProblem(value: string): string | null {
  const trimmed = value.trim()
  if (!trimmed) return 'Bitte Empfängeradresse eingeben.'
  return INVOICE_MAIL_RECIPIENT_PATTERN.test(trimmed)
    ? null
    : 'Bitte eine gültige E-Mail-Adresse eingeben.'
}

/** Templates allowed for the sender's company (#535 multi-company templates). */
export function templatesForSender(
  templates: readonly InvoiceMailTemplateOption[],
  sender: InvoiceMailSender | undefined,
): InvoiceMailTemplateOption[] {
  const companyId = sender?.companyId
  if (!companyId) return []
  return templates.filter((template) => template.companyIds.includes(companyId))
}

/** Keeps the current template when still allowed, otherwise the first allowed one. */
export function nextTemplateId(
  current: string | null,
  allowed: readonly InvoiceMailTemplateOption[],
): string | null {
  if (current && allowed.some((template) => template.id === current)) return current
  return allowed[0]?.id ?? null
}

export function pdfFormatLabel(format: InvoicePdfFormat): string {
  return format === 'zugferd' ? 'ZUGFeRD-PDF (mit eingebetteter XML)' : 'Klassisches PDF'
}

export function fileSize(bytes: number): string {
  return `${Math.max(1, Math.round(bytes / 1024))} KB`
}

/** Only an SMTP-accepted entry with stored journal proof may show green. */
export function archiveText(entry: InvoiceMailLogEntry): string {
  if (entry.status !== 'sent') return 'Kein Archivnachweis'
  return entry.archiveStatus === 'verified'
    ? 'Journal-Kopie archiviert'
    : 'Archivnachweis ausstehend'
}

export function statusText(entry: InvoiceMailLogEntry): string {
  switch (entry.status) {
    case 'sent':
      return 'Versendet'
    case 'failed':
      return 'Versand fehlgeschlagen'
    case 'unknown':
      return 'Versandausgang unklar'
    default:
      return 'Versand läuft oder wurde unterbrochen'
  }
}

export function formatLogTime(iso: string): string {
  return new Intl.DateTimeFormat('de-DE', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'Europe/Berlin',
  }).format(new Date(iso))
}

/** Entries that count as an earlier dispatch (resend needs a conscious confirmation). */
export function previousDispatch(entries: readonly InvoiceMailLogEntry[]) {
  return entries.find((entry) => entry.status !== 'failed')
}

/** RFC 4122 v4; randomUUID exists only in secure contexts. */
export function newAttemptId(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') return globalThis.crypto.randomUUID()
  const bytes = globalThis.crypto.getRandomValues(new Uint8Array(16))
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}
